import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { usePageContent } from '../../hooks/usePageContent';
import { executeMutation } from '../../graphql';
import { subscribeToSwiftMailingList } from '../../graphql/mutations';
import './index.module.css';

const DEFAULT_SECTION_LINKS = [
  { title: 'About',                   href: '/about' },
  { title: 'Premium Collection',      href: '/premium-collection' },
  { title: 'News and Events',         href: '/news-and-events' },
  { title: 'For Businesses',          href: '/business-plates' },
  { title: 'Custom Plates explained', href: '/help' },
  { title: 'Contact Us',              href: '/contact-us' },
];

const DEFAULT_SOCIAL_LINKS = [
  { platform: 'Facebook',  href: 'https://www.facebook.com/viccustomplates', imageSrc: '/images/logos/facebook.svg',  alt: 'Facebook' },
  { platform: 'Instagram', href: 'https://www.instagram.com/vplates/',        imageSrc: '/images/logos/instagram.svg', alt: 'Instagram' },
  { platform: 'YouTube',   href: 'https://www.youtube.com/user/customplates', imageSrc: '/images/logos/youtube.svg',   alt: 'Youtube' },
];

const DEFAULT_GOVERNMENT_LINKS = [
  { title: 'VicRoads',                  href: 'https://www.vicroads.vic.gov.au', imageSrc: '/images/logos/vicroads.svg', alt: 'VicRoads logo' },
  { title: 'Victoria State Government', href: 'https://www.vic.gov.au',          imageSrc: '/images/logos/vic-gov.svg',  alt: 'Victoria State Government logo' },
];

const DEFAULT_LEGAL_LINKS = [
  { title: 'Terms',        href: '/terms' },
  { title: 'Privacy',      href: '/privacy' },
  { title: 'Sitemap',      href: '/sitemap' },
  { title: 'Accessibility',href: '/accessibility' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FOOTER_GROUP_ID = import.meta.env.VITE_FOOTER_GROUP_ID ?? '';

const FooterSubscribeMC = () => {
  const { find } = usePageContent('footer-subscribe');
  const c = find('footerSubscribe') || {};

  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!EMAIL_PATTERN.test(email.trim())) {
      setStatus('error');
      setMessage(c.errorEmail || 'Please enter a valid email address.');
      return;
    }
    if (!agreed) {
      setStatus('error');
      setMessage(c.errorTerms || 'Please agree to the subscription terms.');
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const result = await executeMutation(subscribeToSwiftMailingList, {
        input: {
          email:   email.trim(),
          groupId: FOOTER_GROUP_ID,
        },
      });

      const res = result?.subscribeToSwiftMailingList;
      if (res?.success) {
        setStatus('success');
        setMessage(c.successMessage || 'Thank you for subscribing!');
        setEmail('');
        setAgreed(false);
      } else {
        setStatus('error');
        setMessage(res?.message || c.failureMessage || 'Subscription failed. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage(c.failureMessage || 'Subscription failed. Please try again.');
    }
  };

  const isDisabled = status === 'submitting' || !EMAIL_PATTERN.test(email.trim()) || !agreed;

  return (
    <section className="component subscribe-mc no-fb-index">
      <div className="subscribe-mc__content-container">
        <div className="subscribe-mc__title-container">
          <h2 className="subscribe-mc__title-container-text">{c.heading || 'Subscribe for the latest & more'}</h2>
        </div>
        <div className="subscribe-mc__description-container">
          {c.bodyHtml
            ? <div className="subscribe-mc__description-container-text" dangerouslySetInnerHTML={{ __html: c.bodyHtml }} />
            : <div className="subscribe-mc__description-container-text">
                <p><strong>Keep up with Custom Plates</strong></p> <br />
                <p>Be the first to know about new products, events, competitions and auctions.</p> <br />
                <p>We care about your privacy. Learn more by reading our <a href="/privacy-collection-notice">Collection Notice</a> and <a href="/privacy">Privacy Policy.</a></p>
              </div>
          }
        </div>
        <div id="mc_embed_signup" className="subscribe-mc__form-container">
          <form
            id="mc-embedded-subscribe-form"
            name="mc-embedded-subscribe-form"
            className="validate subscribe-mc__form-container-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <div>
              <div className="mc-field-group">
                <label htmlFor="footer-subscribe-email" style={{ display: 'none' }}>{c.emailLabel || 'Email Address'}</label>
                <input
                  id="footer-subscribe-email"
                  type="email"
                  value={email}
                  placeholder={c.emailPlaceholder || 'Email Address'}
                  className="required email input__ctrl"
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mc-field-group input-group">
                <ul>
                  <li className="subscribe-mc__checklist-inner">
                    <input
                      className="subscribe-mc__checklist-control-input"
                      type="checkbox"
                      id="footer-subscribe-agreement"
                      checked={agreed}
                      onChange={e => setAgreed(e.target.checked)}
                    />
                    <label className="subscribe-mc__checklist-control-label" htmlFor="footer-subscribe-agreement">
                      {c.checkboxLabel || 'By subscribing to the Custom Plates mailing list, I agree to receiving news and promotions about VicRoads Custom Plates and related partner products we think may be of interest to you. Please read our Collection Notice and Privacy Policy for further details about our approach to privacy.'}
                    </label>
                  </li>
                </ul>
              </div>
              {message && (
                <div
                  className={`response ${status === 'success' ? 'response--success' : 'response--error'}`}
                  role="alert"
                >
                  {message}
                </div>
              )}
              <div className="form__actions">
                <button
                  type="submit"
                  className={`button button--tertiary button--no-side-padding${isDisabled ? ' button--disabled' : ''}`}
                  disabled={isDisabled}
                >
                  <span className="button__text">
                    {status === 'submitting' ? (c.submittingText || 'Subscribing...') : (c.submitText || 'Subscribe now')}
                  </span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ content = {} }) => {
  const { pathname } = useLocation();

  const {
    sectionLinks    = DEFAULT_SECTION_LINKS,
    socialLinks     = DEFAULT_SOCIAL_LINKS,
    governmentLinks = DEFAULT_GOVERNMENT_LINKS,
    legalLinks      = DEFAULT_LEGAL_LINKS,
    backToTopText   = 'Back to top',
    backToTopHref   = '#wrapper',
  } = content;

  return (
    <footer className="footer no-fb-index">
      <div id="footer">
        <div className="row">
          {pathname !== '/' && <FooterSubscribeMC />}
          <div className="footer-top footer__top container-fluid">
            <div className="row">
              <nav className="footer__section-links">
                <ul>
                  {sectionLinks.map((item) => (
                    <li key={item.href}>
                      <Link title={item.title} to={item.href}>{item.title}</Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="footer__social-links">
                <nav aria-label="Footer social media links">
                  <ul className="footer__social-links-list">
                    {socialLinks.map((item) => (
                      <li key={item.platform} className="footer__social-links-item">
                        <a href={item.href} className="footer__social-links-link" target="_blank" rel="noopener noreferrer">
                          <img
                            className={`footer__social-links-image${item.platform === 'YouTube' ? ' footer__social-links-image--youtube' : ''}`}
                            src={item.imageSrc}
                            alt={item.alt}
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>

              <div className="footer__government-links">
                <nav aria-label="Footer organisation links">
                  <ul className="footer__government-links-list">
                    {governmentLinks.map((item) => (
                      <li key={item.href} className="footer__government-links-item">
                        <a href={item.href} className="footer__government-links-link" target="_blank" rel="noopener noreferrer">
                          <img
                            className={item.title === 'VicRoads' ? 'footer__government-links-image footer__government-links-image--vicroads' : 'footer__government-links-image'}
                            src={item.imageSrc}
                            alt={item.alt}
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>
          </div>

          <div className="footer-bottom footer__bottom container-fluid">
            <nav className="footer__other-links">
              <ul>
                {legalLinks.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href}>{item.title}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <a href={backToTopHref} className="footer__back-to-top">
            <span className="visuallyhidden">{backToTopText}</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  content: PropTypes.shape({
    sectionLinks: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })),
    socialLinks: PropTypes.arrayOf(PropTypes.shape({
      platform: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      imageSrc: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })),
    governmentLinks: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      imageSrc: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })),
    legalLinks: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })),
    backToTopText: PropTypes.string,
    backToTopHref: PropTypes.string,
  }),
};

export default Footer;
